import { extractUrls, isUsableUrlTitle, legacyPathTitle, urlDisplayTitle, type Note, type UrlEntity } from '../../domain/note'
import { urlHostname } from '../../domain/urlCategory'
import { APP_SETTINGS } from '../../config/appSettings'
import type { IconName } from '../../components/Icon'
import { hasSpecificLinkImage, linkPreviewImageUrl } from '../linkMetadata'
import { linkMetadataUrlKey } from '../linkMetadataWork'
import { linkPreviewPresentation, type LinkPreviewCategory } from '../linkPreviewCategory'
import { linkSummary } from '../linkSummary'
import { sameSubject } from '../mergeUnpacked'
import type { UnpackedCard } from '../../screenshotCleanup/card'

/**
 * The Links screen's model (docs/link-notes/PLAN.md, phase 7): every saved link of the account as one flat list, grouped into topics, filtered
 * and searched. Pure code over the notes; no model call lives here. A link saved in several notes is one entry (the same key the gist and answers
 * already use), carrying every note it is in.
 */
export const LIBRARY_SETTINGS = APP_SETTINGS.links.library

/** Whether this build shows the Links screen: on in staging through VITE_LINKS_LIBRARY, or once `links.library.enabled` ships. */
export function libraryEnabled(): boolean {
  return LIBRARY_SETTINGS.enabled || import.meta.env.VITE_LINKS_LIBRARY === 'on'
}

export type TopicSource = 'owner' | 'tag' | 'kind'

export interface LibraryLink {
  key: string
  url: UrlEntity
  /** The note to open for this link: the most recently edited one that holds it. */
  noteId: string
  noteIds: string[]
  title: string
  category: LinkPreviewCategory
  kindLabel: string
  kindPlural: string
  icon: IconName
  provider: string
  /** The page's picture, only when it is specific to this page (never a site logo). */
  image: string | null
  /** The generated gist, else the site's own description. */
  summary: string | null
  summarySource: 'gist' | 'site' | null
  /** The owner's own words beside the link, when the note holds nothing but this link and them. */
  words: string | null
  topic: string
  topicKey: string
  topicSource: TopicSource
  pinnedAt: number | null
  addedAt: number
}

const COLLAPSE = /\s+/g
const URL_IN_TEXT = /https?:\/\/\S+/gi
const MARKDOWN_LINK = /!?\[([^\]]*)\]\([^)]*\)/g

/** What a note says besides its link, on one line. Only for a note that holds one link; otherwise whose words they are is unclear. */
function ownText(note: Pick<Note, 'body' | 'urls'>): string | null {
  if (note.urls.length !== 1) return null
  return note.body.replace(MARKDOWN_LINK, '$1').replace(URL_IN_TEXT, ' ').replace(/^[\s#>*\-+]+/gm, ' ').replace(COLLAPSE, ' ').trim() || null
}

/** The label the owner (or the app they shared from) put on this very link: `[Label](address)`, unless the label is just the address. */
export function authoredLabel(body: string, url: UrlEntity): string | null {
  const key = linkMetadataUrlKey(url)
  for (const match of body.matchAll(/(?<!!)\[((?:\\.|[^\]\\\n])+)\]\(([^()\s]+)(?:\s+["'][^"']*["'])?\)/g)) {
    const label = match[1]!.replace(/\\(.)/g, '$1').trim()
    if (label.length < 3 || /https?:\/\//i.test(label)) continue
    const target = extractUrls(match[2]!)[0]
    if (!target || linkMetadataUrlKey(target) !== key) continue
    // A label the app itself made from the address when the link was pasted ("Expose") is not the owner's.
    if (label === urlDisplayTitle({ ...target, title: null }) || label === legacyPathTitle(target.canonicalUrl ?? target.originalUrl)) continue
    return label
  }
  return null
}

/**
 * What the link is called here. The page's own title when it names the page; else the label the owner (or the app they shared from) wrote
 * for it, else the first line they wrote beside a lone link ("Rental unit in Barcelona · ★4.85" shared from a rental app), else the best
 * the address itself can say. Never a redirect notice, a route word or an id.
 */
export function linkTitle(note: Pick<Note, 'body' | 'urls'>, url: UrlEntity): string {
  if (isUsableUrlTitle(url, url.title)) return urlDisplayTitle(url)
  const label = authoredLabel(note.body, url)
  if (label) return label
  // The first line the owner wrote, beside a lone link.
  const own = note.urls.length === 1
    ? note.body.replace(MARKDOWN_LINK, '$1').replace(URL_IN_TEXT, ' ').split('\n').map((value) => value.replace(/^[\s#>*\-+]+/, '').replace(COLLAPSE, ' ').trim()).find(Boolean)
    : null
  if (own && own.length >= 8) return own.length <= 90 ? own : `${own.slice(0, 88).replace(/\s\S*$/, '')}…`
  return urlDisplayTitle(url)
}

/** The owner's own words about a link: what a note says besides the link itself. Only for a note that holds one link; otherwise whose words they are is unclear. */
export function ownerWords(note: Pick<Note, 'body' | 'urls'>, title: string): string | null {
  const text = ownText(note)
  if (!text) return null
  // Words that only repeat the link's own title (a shared page's "Title URL") are not the owner's.
  const plain = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
  if (text.length < 2 || (plain(title) && (plain(text).includes(plain(title)) || plain(title).includes(plain(text))))) return null
  return text.length <= 160 ? text : `${text.slice(0, 158).replace(/\s\S*$/, '')}…`
}

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)
export const topicKeyOf = (name: string) => name.trim().replace(COLLAPSE, ' ').toLowerCase()

/** A tag worth naming a topic after: not a namespaced one such as an import's provenance (`minimany-import:…`). */
const usableTag = (tag: string) => tag.length > 0 && !tag.includes(':')

/**
 * The premade collections, in the order they are shown. A link goes into the one its kind belongs to; a collection appears only once it
 * holds a link. The set grows only for a link none of them fits and whose note carries a tag of the owner's (see `settleTags`).
 */
export const PREMADE_TOPICS = ['Articles', 'Recipes', 'Products', 'Places', 'Videos', 'Music', 'Podcasts', 'Films/TV', 'Coding'] as const
export const OTHER_TOPIC = 'Other'
export const OTHER_TOPIC_KEY = topicKeyOf(OTHER_TOPIC)
const PREMADE_KEYS: readonly string[] = PREMADE_TOPICS.map(topicKeyOf)

const PODCAST_HOSTS = ['podcasts.apple.com', 'pca.st', 'overcast.fm', 'podcasts.google.com', 'castbox.fm', 'pocketcasts.com', 'podbean.com', 'castro.fm']
/** Audio is a podcast on a podcast host, or on Spotify's show and episode addresses; every other audio link is music. */
function isPodcast(url: UrlEntity): boolean {
  const host = urlHostname(url)
  if (PODCAST_HOSTS.some((domain) => host === domain || host.endsWith(`.${domain}`))) return true
  let path = ''
  try { path = new URL(url.canonicalUrl ?? url.originalUrl).pathname.toLowerCase() } catch { /* an address that does not parse has no path */ }
  return /(^|\.)spotify\.com$/.test(host) ? /\/(show|episode)\//.test(path) : /\/podcasts?\//.test(path)
}

/** The premade collection a kind of link belongs to, or null when none fits. */
export function premadeTopic(category: LinkPreviewCategory, url: UrlEntity): (typeof PREMADE_TOPICS)[number] | null {
  switch (category) {
    case 'article': case 'document': case 'reference': case 'book': return 'Articles'
    case 'recipe': return 'Recipes'
    case 'product': return 'Products'
    case 'place': case 'listing': return 'Places'
    case 'video': return 'Videos'
    case 'audio': return isPodcast(url) ? 'Podcasts' : 'Music'
    case 'movie': return 'Films/TV'
    case 'code': return 'Coding'
    default: return null
  }
}

/**
 * A link's topic: the owner's choice, else the premade collection its kind belongs to, else (nothing fits) the first tag the owner gave
 * its note, else Other.
 */
export function suggestTopic(note: Pick<Note, 'userTags'>, url: UrlEntity, category: LinkPreviewCategory): { name: string; source: TopicSource } {
  const chosen = url.topic?.trim().replace(COLLAPSE, ' ')
  if (chosen) return { name: chosen, source: 'owner' }
  const premade = premadeTopic(category, url)
  if (premade) return { name: premade, source: 'kind' }
  const tag = note.userTags.map((value) => value.trim()).find(usableTag)
  if (tag) return { name: sentenceCase(tag), source: 'tag' }
  return { name: OTHER_TOPIC, source: 'kind' }
}

/**
 * A collection made from a tag has to earn its place: fewer than `minTopicLinks` links, or past `maxTopicTiles`, and those links go to Other,
 * so a long tail of one-off tags never becomes a wall of collections. The owner's own choices are never moved.
 */
function settleTags(links: LibraryLink[], settings = LIBRARY_SETTINGS): void {
  const byKey = new Map<string, LibraryLink[]>()
  for (const link of links) if (link.topicSource === 'tag') byKey.set(link.topicKey, [...byKey.get(link.topicKey) ?? [], link])
  const keep = new Set([...byKey].filter(([, group]) => group.length >= settings.minTopicLinks)
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0])).slice(0, settings.maxTopicTiles).map(([key]) => key))
  for (const link of links) {
    if (link.topicSource !== 'tag' || keep.has(link.topicKey)) continue
    link.topic = OTHER_TOPIC; link.topicKey = OTHER_TOPIC_KEY; link.topicSource = 'kind'
  }
}

const live = (note: Note) => note.status !== 'trash' && note.status !== 'expired'

/**
 * A link is a recipe when the note it is in was unpacked as one and the link is that recipe: the note's only link, or one that shares the
 * unpacked reading's words. The unpack read the dish off the screenshot; the page itself often says nothing (no schema, no "recipe" anywhere).
 */
function unpackedRecipe(entries: readonly { note: Note; url: UrlEntity }[], unpacked: ReadonlyMap<string, UnpackedCard>): boolean {
  return entries.some(({ note, url }) => {
    const card = unpacked.get(note.id)
    return card?.kind === 'recipe' && (note.urls.length === 1 || sameSubject(url, card))
  })
}

export function buildLibrary(notes: readonly Note[], unpacked: ReadonlyMap<string, UnpackedCard> = new Map()): LibraryLink[] {
  const grouped = new Map<string, { note: Note; url: UrlEntity }[]>()
  for (const note of notes) {
    if (!live(note)) continue
    for (const url of note.urls) {
      const key = linkMetadataUrlKey(url)
      const entries = grouped.get(key)
      if (entries) entries.push({ note, url }); else grouped.set(key, [{ note, url }])
    }
  }
  const links: LibraryLink[] = []
  for (const [key, entries] of grouped) {
    entries.sort((a, b) => b.note.modifiedAt - a.note.modifiedAt)
    const { note, url } = entries[0]!
    const presentation = linkPreviewPresentation(url, note.contentType, unpackedRecipe(entries, unpacked) ? 'recipe' : undefined)
    const title = linkTitle(note, url)
    const kindPlural = presentation.pluralLabel
    const topic = suggestTopic(note, url, presentation.category)
    const summary = linkSummary(url, null)
    const pinned = entries.map((entry) => entry.url.pinnedAt).filter((value): value is number => typeof value === 'number')
    // A summary shown on a row is a line, not the whole gist: cut at a word.
    const max = LIBRARY_SETTINGS.summaryMaxChars
    links.push({
      key, url, noteId: note.id, noteIds: entries.map((entry) => entry.note.id), title,
      category: presentation.category, kindLabel: presentation.label, kindPlural, icon: presentation.icon, provider: presentation.provider,
      image: hasSpecificLinkImage(url, url.imageUrl) ? linkPreviewImageUrl(url.imageUrl) : null,
      summary: summary.text && summary.text.length > max ? `${summary.text.slice(0, max).replace(/\s\S*$/, '')}…` : summary.text,
      summarySource: summary.source,
      words: ownerWords(note, title),
      topic: topic.name, topicKey: topicKeyOf(topic.name), topicSource: topic.source,
      pinnedAt: pinned.length ? Math.max(...pinned) : null,
      addedAt: Math.min(...entries.map((entry) => entry.url.addedAt ?? entry.note.createdAt)),
    })
  }
  settleTags(links)
  return links.sort(newestFirst)
}

/** Pinned first (the most recently pinned on top), then the newest saved. */
function newestFirst(a: LibraryLink, b: LibraryLink): number {
  return (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0) || b.addedAt - a.addedAt || a.title.localeCompare(b.title)
}

export const sortByRecency = (links: readonly LibraryLink[]) => [...links].sort((a, b) => b.addedAt - a.addedAt || a.title.localeCompare(b.title))

/* ---------- Topics ---------- */

export interface LibraryTopic {
  key: string
  name: string
  links: LibraryLink[]
  /** Kinds in this topic, most common first, for the tile's one-line mix. */
  mix: { category: LinkPreviewCategory; label: string; plural: string; count: number }[]
}

function toTopic(key: string, name: string, links: LibraryLink[]): LibraryTopic {
  const counts = new Map<LinkPreviewCategory, { label: string; plural: string; count: number }>()
  for (const link of links) {
    const entry = counts.get(link.category)
    if (entry) entry.count++; else counts.set(link.category, { label: link.kindLabel, plural: link.kindPlural, count: 1 })
  }
  return { key, name, links, mix: [...counts].map(([category, value]) => ({ category, ...value })).sort((a, b) => b.count - a.count) }
}

/**
 * The collections of a library that hold at least one link: the premade ones in their fixed order (a place that is empty is not shown),
 * then the owner's and the tags' own, largest first, and Other last.
 */
export function groupTopics(links: readonly LibraryLink[]): LibraryTopic[] {
  const byKey = new Map<string, LibraryLink[]>()
  const names = new Map<string, string>()
  for (const link of links) {
    byKey.set(link.topicKey, [...byKey.get(link.topicKey) ?? [], link])
    // The owner's spelling wins when links disagree on case.
    if (!names.has(link.topicKey) || link.topicSource === 'owner') names.set(link.topicKey, link.topic)
  }
  const rank = (key: string) => key === OTHER_TOPIC_KEY ? 2 : PREMADE_KEYS.includes(key) ? 0 : 1
  return [...byKey].map(([key, group]) => toTopic(key, names.get(key)!, group))
    .sort((a, b) => rank(a.key) - rank(b.key)
      || (rank(a.key) === 0 ? PREMADE_KEYS.indexOf(a.key) - PREMADE_KEYS.indexOf(b.key) : b.links.length - a.links.length || a.name.localeCompare(b.name)))
}

/** Every collection a link can be moved to: the premade ones, Other, and any the owner or a tag made (owner spelling first). */
export function topicNames(links: readonly LibraryLink[]): string[] {
  const names = new Map<string, string>(PREMADE_TOPICS.map((name) => [topicKeyOf(name), name]))
  for (const link of links) if (!names.has(link.topicKey) || link.topicSource === 'owner') names.set(link.topicKey, link.topic)
  names.delete(OTHER_TOPIC_KEY)
  return [...[...names.values()].sort((a, b) => a.localeCompare(b)), OTHER_TOPIC]
}

/* ---------- Facets, recent, new ---------- */

export function kindCounts(links: readonly LibraryLink[]): { category: LinkPreviewCategory; label: string; plural: string; icon: IconName; count: number }[] {
  const counts = new Map<LinkPreviewCategory, { label: string; plural: string; icon: IconName; count: number }>()
  for (const link of links) {
    const entry = counts.get(link.category)
    if (entry) entry.count++; else counts.set(link.category, { label: link.kindLabel, plural: link.kindPlural, icon: link.icon, count: 1 })
  }
  return [...counts].map(([category, value]) => ({ category, ...value })).sort((a, b) => b.count - a.count)
}

export const filterKinds = (links: readonly LibraryLink[], kinds: ReadonlySet<LinkPreviewCategory>) =>
  kinds.size ? links.filter((link) => kinds.has(link.category)) : [...links]

const DAY = 86_400_000
export interface RecentGroup { id: 'week' | 'month' | 'earlier'; label: string; links: LibraryLink[] }

export function recentGroups(links: readonly LibraryLink[], now: number, settings = LIBRARY_SETTINGS): RecentGroup[] {
  const ordered = sortByRecency(links)
  const week = ordered.filter((link) => now - link.addedAt <= settings.recentWeekDays * DAY)
  const month = ordered.filter((link) => !week.includes(link) && now - link.addedAt <= settings.recentMonthDays * DAY)
  const earlier = ordered.filter((link) => !week.includes(link) && !month.includes(link))
  return ([
    { id: 'week', label: 'This week', links: week }, { id: 'month', label: 'Earlier this month', links: month }, { id: 'earlier', label: 'Before that', links: earlier },
  ] as RecentGroup[]).filter((group) => group.links.length)
}

/** Links saved since the owner last opened the Links screen, newest first. `since` null (never opened) shows none. */
export function savedSince(links: readonly LibraryLink[], since: number | null, max = LIBRARY_SETTINGS.newStripMax): LibraryLink[] {
  return since === null ? [] : sortByRecency(links).filter((link) => link.addedAt > since).slice(0, max)
}

/* ---------- Search ---------- */

export interface LibraryMatch {
  link: LibraryLink
  /** 1: the title, source, summary, the owner's words or topic. 2: only inside an answer the owner asked for. */
  tier: 1 | 2
  /** The text the match was found in, cut around it, for the row to show with the words marked. */
  snippet: { text: string; before: boolean; after: boolean; field: 'summary' | 'answer' } | null
}

export const searchWords = (query: string) => query.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 1)

function snippetAround(text: string, words: readonly string[], length = 130) {
  const lower = text.toLowerCase()
  const at = Math.min(...words.map((word) => lower.indexOf(word)).filter((index) => index >= 0))
  if (!Number.isFinite(at) || text.length <= length) return { text, before: false, after: false }
  let start = Math.max(0, at - 40)
  if (start > 0) { const space = text.indexOf(' ', start); if (space > 0 && space < at) start = space + 1 }
  let end = Math.min(text.length, start + length)
  if (end < text.length) { const space = text.lastIndexOf(' ', end); if (space > at) end = space }
  return { text: text.slice(start, end), before: start > 0, after: end < text.length }
}

export function searchLibrary(links: readonly LibraryLink[], query: string): { words: string[]; matches: LibraryMatch[] } {
  const words = searchWords(query)
  if (!words.length) return { words, matches: [] }
  const matches: (LibraryMatch & { score: number })[] = []
  for (const link of links) {
    const fields = [link.title, link.provider, urlHostname(link.url), link.summary ?? '', link.words ?? '', link.topic].join(' ').toLowerCase()
    const answers = (link.url.answers ?? []).map((answer) => `${answer.prompt}. ${answer.text}`)
    if (words.every((word) => fields.includes(word))) {
      const inSummary = link.summary && words.some((word) => link.summary!.toLowerCase().includes(word))
      matches.push({ link, tier: 1, snippet: inSummary ? { ...snippetAround(link.summary!, words), field: 'summary' } : null,
        score: (words.some((word) => link.title.toLowerCase().includes(word)) ? 4 : 0) + (link.words && words.some((word) => link.words!.toLowerCase().includes(word)) ? 2 : 0) + (link.pinnedAt ? 1 : 0) })
    } else {
      const hit = answers.find((text) => words.every((word) => `${fields} ${text.toLowerCase()}`.includes(word)) && words.some((word) => text.toLowerCase().includes(word)))
      if (hit) matches.push({ link, tier: 2, snippet: { ...snippetAround(hit, words), field: 'answer' }, score: 0 })
    }
  }
  matches.sort((a, b) => a.tier - b.tier || b.score - a.score || b.link.addedAt - a.link.addedAt)
  return { words, matches: matches.map(({ score: _score, ...match }) => match) }
}

/* ---------- Which layout ---------- */

/** A small library is one list; past `listBelowLinks` it gets topics (docs/link-notes/PLAN.md, phase 7). */
export const libraryShape = (count: number, settings = LIBRARY_SETTINGS): 'list' | 'topics' => count < settings.listBelowLinks ? 'list' : 'topics'
